import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toggleTicketFilters } from '../../../../actions/ui';
import { TablePagination } from '../shared/table/TablePagination';
import {
    getTicketPriorityLabel,
    getTicketTypeLabel,
    TICKET_TYPE_OPTIONS,
    TICKET_STATUS_OPTIONS,
} from '../../../../helpers/ticket-label-helper';
import { TicketStatusNoteModal } from './shared';
import {
    useTaskListScreen,
} from './hooks';
import {
    TaskAssigneeModal,
    TaskBulkActionsBar,
    TaskListEmptyState,
    TaskListFiltersDrawer,
    TaskListStatsCards,
    TaskListTableSkeleton,
} from './components';
import {
    formatDueDate,
    isTicketOverdue,
    priorityBadgeMap,
    searchFieldOptions,
    statusDotMap,
    statusInitialClassMap,
    statusInitialMap,
    statusOptions,
    taskListTicketTypeIconMap,
} from './helpers';

export const TaskListScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token } = useSelector((state) => state.auth);
    const { statsCollapsed, ticketFiltersCollapsed, ticketCreatedAt } = useSelector((state) => state.ui);

    const {
        tickets,
        isLoading,
        error,
        searchField,
        searchTerm,
        statusFilter,
        typeFilter,
        customerFilter,
        projectFilter,
        customerOptions,
        projectOptions,
        limit,
        offset,
        pagination,
        dashboardStats,
        hoveredPreview,
        selectedTicketIds,
        updatingAssigneeIds,
        isStatusModalOpen,
        statusModalMode,
        statusModalTargetStatus,
        statusModalNote,
        statusModalIsPublic,
        statusModalAttachments,
        statusModalError,
        isSavingStatusModal,
        isAssigneeModalOpen,
        assigneeModalTicket,
        assigneeSearchTerm,
        assigneeOptions,
        isAssigneeSearching,
        assigneeModalError,
        sortedTickets,
        allVisibleSelected,
        statusModalTitle,
        statusModalSubtitle,
        getSortIcon,
        handleSort,
        showDescriptionPreview,
        hideDescriptionPreview,
        setSearchField,
        setSearchTerm,
        setStatusFilter,
        setStatusGroupFilter,
        setTypeFilter,
        setCustomerFilter,
        setProjectFilter,
        setLimit,
        setOffset,
        handleClearFilters,
        handleDashboardCardFilter,
        isDashboardCardActive,
        handleToggleTicketSelection,
        handleToggleSelectAllVisible,
        openStatusModalForBulk,
        handleStatusChangeIntent,
        handleOpenTicketDetail,
        openAssigneeModal,
        closeAssigneeModal,
        setAssigneeSearchTerm,
        handleSelectAssigneeFromModal,
        setStatusModalTargetStatus,
        setStatusModalNote,
        setStatusModalIsPublic,
        setStatusModalAttachments,
        handleRemoveModalAttachment,
        closeStatusModal,
        handleSubmitStatusModal,
        setSelectedTicketIds,
    } = useTaskListScreen({ token, dispatch, navigate, ticketCreatedAt });

    const renderSortableHeader = (field, label) => (
        <button
            type="button"
            className="btn btn-link p-0 text-decoration-none fw-semibold text-dark"
            onClick={() => handleSort(field)}
        >
            <span className="fs-12 text-muted me-2">{getSortIcon(field)}</span> {label}
        </button>
    );

    const handleCloseFiltersDrawer = React.useCallback(() => {
        dispatch(toggleTicketFilters());
    }, [dispatch]);

    const handleResetFiltersFromDrawer = React.useCallback(() => {
        handleClearFilters();
        setOffset(0);
    }, [handleClearFilters, setOffset]);

    const hasActiveFilters = React.useMemo(() => {
        const normalizedSearch = String(searchTerm || '').trim();
        const hasStatusFilter = statusFilter && statusFilter !== 'ALL';
        const hasTypeFilter = typeFilter && typeFilter !== 'ALL';
        const hasCustomerFilter = customerFilter && customerFilter !== 'ALL';
        const hasProjectFilter = projectFilter && projectFilter !== 'ALL';

        return Boolean(
            normalizedSearch
            || hasStatusFilter
            || hasTypeFilter
            || hasCustomerFilter
            || hasProjectFilter,
        );
    }, [searchTerm, statusFilter, typeFilter, customerFilter, projectFilter]);

    return (
        <div className="row">
            <div className="col-12">
                <div className="card stretch stretch-full card__no-border">
                    <div className="card-body pt-3">
                        <TaskListStatsCards
                            isVisible={statsCollapsed}
                            dashboardStats={dashboardStats}
                            isDashboardCardActive={isDashboardCardActive}
                            onDashboardCardFilter={handleDashboardCardFilter}
                        />

                        <TaskBulkActionsBar
                            selectedCount={selectedTicketIds.length}
                            onBulkStatusChange={openStatusModalForBulk}
                            onClearSelection={() => setSelectedTicketIds([])}
                        />

                        {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}

                        <div className="table-responsive ticket-ops-table-wrap">
                            {!isLoading && tickets.length === 0 ? (
                                <TaskListEmptyState
                                    hasActiveFilters={hasActiveFilters}
                                    onResetFilters={handleResetFiltersFromDrawer}
                                />
                            ) : (
                                <table className="table mb-0 align-middle ticket-ops-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={allVisibleSelected}
                                                    onChange={(event) => handleToggleSelectAllVisible(event.target.checked)}
                                                    aria-label="Seleccionar todos"
                                                />
                                            </th>
                                            <th>{renderSortableHeader('code', 'Código')}</th>
                                            <th>{renderSortableHeader('title', 'Título')}</th>
                                            <th>{renderSortableHeader('customer', 'Cliente')}</th>
                                            <th>{renderSortableHeader('project', 'Proyecto')}</th>
                                            <th>{renderSortableHeader('assignee', 'Asignado')}</th>
                                            <th>{renderSortableHeader('priority', 'Prioridad')}</th>
                                            <th>{renderSortableHeader('type', 'Tipo')}</th>
                                            <th>{renderSortableHeader('status', 'Estatus')}</th>
                                            <th>{renderSortableHeader('dueDate', 'Vence')}</th>
                                            <th>{renderSortableHeader('createdDate', 'Creado')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading && (
                                            <TaskListTableSkeleton rows={6} />
                                        )}

                                        {!isLoading && sortedTickets.map((ticket) => {
                                            const isSelected = selectedTicketIds.some((ticketId) => String(ticketId) === String(ticket._id));
                                            const isAssigneeLoading = updatingAssigneeIds.includes(String(ticket._id));
                                            const assigneeLabel = ticket?.assignee?.name || ticket?.assignee?.email || 'Sin asignar';
                                            const hasAssignee = Boolean(ticket?.assignee?._id);
                                            const isAssigneeAvailable = Boolean(ticket?.assignee?.available);

                                            return (
                                                <tr key={ticket._id} className={isSelected ? 'ticket-ops-row is-selected' : 'ticket-ops-row'}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={isSelected}
                                                            onChange={() => handleToggleTicketSelection(ticket._id)}
                                                            aria-label={`Seleccionar ${ticket.code || ticket._id}`}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className={`ticket-status-initial ${statusInitialClassMap[ticket.status] || 'ticket-status-initial--secondary'}`}>
                                                                {statusInitialMap[ticket.status] || '?'}
                                                            </span>
                                                            <div
                                                                className="link p-0 text-decoration-none fw-semibold"
                                                                style={{ fontSize: 12 }}
                                                                onClick={() => handleOpenTicketDetail(ticket)}
                                                            >
                                                                {ticket.code || '-'}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div
                                                            className="position-relative"
                                                            onMouseEnter={(event) => showDescriptionPreview(ticket, event.currentTarget)}
                                                            onMouseLeave={hideDescriptionPreview}
                                                        >
                                                            <span className="fw-bold text-truncate" style={{ maxWidth: '300px' }}>
                                                                {ticket.title || '-'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>{ticket.customer?.name || '-'}</td>
                                                    <td>{ticket.project?.name || '-'}</td>
                                                    <td style={{ minWidth: '220px' }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-light-brand btn-sm text-start w-100"
                                                            onClick={() => openAssigneeModal(ticket._id)}
                                                            disabled={isAssigneeLoading}
                                                        >
                                                            {isAssigneeLoading ? 'Actualizando...' : (
                                                                <span className="d-inline-flex align-items-center gap-2">
                                                                    {hasAssignee && (
                                                                        <i
                                                                            className={`rounded-circle ${isAssigneeAvailable ? 'bg-success' : 'bg-danger'}`}
                                                                            style={{ width: '8px', height: '8px', display: 'inline-block' }}
                                                                        />
                                                                    )}
                                                                    <span>{assigneeLabel}</span>
                                                                </span>
                                                            )}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${priorityBadgeMap[String(ticket.priority || '').toUpperCase()] || 'bg-soft-secondary text-secondary'}`}>
                                                            {getTicketPriorityLabel(ticket.priority)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="d-inline-flex align-items-center gap-1">
                                                            <i className={taskListTicketTypeIconMap[String(ticket.type || '').toUpperCase()] || 'bi bi-tag'} />
                                                            <span>{getTicketTypeLabel(ticket.type)}</span>
                                                        </span>
                                                    </td>
                                                    <td style={{ minWidth: '220px' }}>
                                                        <div className="d-inline-flex align-items-center gap-2 w-100">
                                                            <span className={`ticket-status-dot ticket-status-dot--${statusDotMap[ticket.status] || 'secondary'}`} />
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={ticket.status || ''}
                                                                onChange={(event) => handleStatusChangeIntent(ticket, event.target.value)}
                                                            >
                                                                {TICKET_STATUS_OPTIONS.map((option) => (
                                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className={isTicketOverdue(ticket) ? 'text-danger fw-semibold' : ''}>{formatDueDate(ticket)}</div>
                                                        {(ticket.resolutionDueDate || ticket.firstResponseDueDate) && (
                                                            <div className="text-muted fs-12">
                                                                {new Date(ticket.resolutionDueDate || ticket.firstResponseDueDate).toLocaleString('es-MX')}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{ticket.createdDate ? new Date(ticket.createdDate).toLocaleString('es-MX') : '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <TablePagination
                            total={pagination.total}
                            limit={limit}
                            offset={offset}
                            hasNext={pagination.hasNext}
                            hasPrev={pagination.hasPrev}
                            onOffsetChange={setOffset}
                            isLoading={isLoading}
                        />

                        {hoveredPreview && (
                            <div
                                className="ticket-description-hover bg-white border rounded shadow-sm p-2"
                                style={{
                                    position: 'fixed',
                                    zIndex: 1080,
                                    top: hoveredPreview.top,
                                    left: hoveredPreview.left,
                                    width: `${hoveredPreview.width}px`,
                                    pointerEvents: 'none',
                                }}
                            >
                                <div className="fw-semibold mb-1">Descripción</div>
                                <div className="text-muted fs-12" style={{ whiteSpace: 'normal' }}>
                                    {hoveredPreview.description}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <TaskListFiltersDrawer
                isOpen={ticketFiltersCollapsed}
                onClose={handleCloseFiltersDrawer}
                searchField={searchField}
                searchFieldOptions={searchFieldOptions}
                onSearchFieldChange={(nextValue) => { setSearchField(nextValue); setOffset(0); }}
                searchTerm={searchTerm}
                onSearchTermChange={(nextValue) => { setSearchTerm(nextValue); setOffset(0); }}
                statusFilter={statusFilter}
                statusOptions={statusOptions}
                onStatusFilterChange={(nextValue) => {
                    setStatusGroupFilter('');
                    setStatusFilter(nextValue);
                    setOffset(0);
                }}
                customerFilter={customerFilter}
                customerOptions={[{ value: 'ALL', label: 'Todos' }, ...customerOptions]}
                onCustomerFilterChange={(nextValue) => { setCustomerFilter(nextValue); setOffset(0); }}
                projectFilter={projectFilter}
                projectOptions={[{ value: 'ALL', label: 'Todos' }, ...projectOptions]}
                onProjectFilterChange={(nextValue) => { setProjectFilter(nextValue); setOffset(0); }}
                typeFilter={typeFilter}
                typeOptions={[{ value: 'ALL', label: 'Todos' }, ...TICKET_TYPE_OPTIONS]}
                onTypeFilterChange={(nextValue) => { setTypeFilter(nextValue); setOffset(0); }}
                rowsPerPage={limit}
                rowsOptions={[10, 25, 50]}
                onRowsPerPageChange={(nextValue) => { setLimit(Number(nextValue)); setOffset(0); }}
                onClearFilters={handleResetFiltersFromDrawer}
            />

            <TicketStatusNoteModal
                isOpen={isStatusModalOpen}
                title={statusModalTitle}
                subtitle={statusModalSubtitle}
                status={statusModalTargetStatus}
                statusOptions={TICKET_STATUS_OPTIONS}
                allowStatusEdit={statusModalMode === 'bulk'}
                onStatusChange={setStatusModalTargetStatus}
                note={statusModalNote}
                onNoteChange={setStatusModalNote}
                isPublic={statusModalIsPublic}
                onIsPublicChange={setStatusModalIsPublic}
                attachments={statusModalAttachments}
                onAttachmentsChange={setStatusModalAttachments}
                onAttachmentRemove={handleRemoveModalAttachment}
                isSaving={isSavingStatusModal}
                error={statusModalError}
                onClose={closeStatusModal}
                onSubmit={handleSubmitStatusModal}
                submitLabel={statusModalMode === 'bulk' ? 'Aplicar cambios' : 'Guardar cambio'}
            />

            <TaskAssigneeModal
                isOpen={isAssigneeModalOpen}
                ticket={assigneeModalTicket}
                searchTerm={assigneeSearchTerm}
                setSearchTerm={setAssigneeSearchTerm}
                options={assigneeOptions}
                isSearching={isAssigneeSearching}
                error={assigneeModalError}
                onSelectAssignee={handleSelectAssigneeFromModal}
                onClose={closeAssigneeModal}
            />
        </div>
    );
};
